import React, { useEffect, useState } from 'react';
import { Button, Flex, FlexProps, Text } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';

import arrowIcon from '../../Resources/Images/Icons/arrow_icon.svg';
import { DocumentType } from '../../Utils/backStageApi';
import { GenericTable } from '../Common/GenericTable';
import { TablePager } from '../Common/TablePager';

export type FileManagerDocument = DocumentType & { file_size?: number | null };

interface Props extends Pick<FlexProps, 'mt'> {
  documents: FileManagerDocument[];
  sortDirection: 'asc' | 'desc';
  onSortToggle: () => void;
}

const PAGE_SIZE_OPTIONS = [10, 20] as const;

const TABLE_CELL_TEXT_STYLE = {
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.01em',
  color: '#00292B',
};

const formatFileSize = (size?: number | null): string => {
  if (size == null || Number.isNaN(size)) return '-';
  if (size < 1024) return `${size}b`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)}kb`;
  const mb = size / (1024 * 1024);
  const rounded = Math.round(mb * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}mb` : `${rounded.toFixed(1)}mb`;
};

const formatDisplayDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const normalized = dateStr.replace(/\./g, '/').replace(/-/g, '/').trim();
  const parts = normalized.split('/').map((p) => p.trim());
  if (parts.length >= 3) {
    const [year, month, day] = parts;
    return `${Number(year)}/${Number(month)}/${Number(day)}`;
  }
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return dateStr;
  return `${parsed.getFullYear()}/${parsed.getMonth() + 1}/${parsed.getDate()}`;
};

export const FileManagerTable: React.FC<Props> = ({ documents, sortDirection, onSortToggle, mt }) => {
  const history = useHistory();
  const [pageSize, setPageSize] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const totalItems = documents.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = totalItems === 0 ? 0 : (safePage - 1) * pageSize;
  const paginated = documents.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [documents]);

  const columns = [
    {
      label: '上傳日期',
      width: '230px',
      paddingLeft: '40px',
      sortDirection,
      onSort: onSortToggle,
      sortArrowSrc: arrowIcon,
      sortArrowAlt: '排序',
    },
    { label: '標題', width: '510px' },
    { label: '檔案大小', width: '200px' },
    { label: '', width: '100px' },
  ];

  const renderRow = (item: FileManagerDocument) => (
    <Flex key={item.did} w="1040px" minH="60px" align="stretch">
      <Flex w="230px" minW="230px" align="center" px="40px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE}>{formatDisplayDate(item.date)}</Text>
      </Flex>

      <Flex w="510px" minW="510px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.title}>
          {item.title}
        </Text>
      </Flex>

      <Flex w="200px" minW="200px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE}>{formatFileSize(item.file_size)}</Text>
      </Flex>

      <Flex w="100px" minW="100px" justify="center" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Button
          variant="unstyled"
          minW="37px"
          h="22px"
          onClick={() =>
            history.push('/bs/document/edit', {
              did: item.did,
              title: item.title,
              link: item.link,
            })
          }
        >
          <Text
            fontFamily="'Open Sans', sans-serif"
            fontSize="18px"
            fontWeight={600}
            lineHeight="120%"
            letterSpacing="0.01em"
            textDecoration="underline"
            color="#006699"
          >
            編輯
          </Text>
        </Button>
      </Flex>
    </Flex>
  );

  return (
    <Flex direction="column" w="1040px" mt={mt}>
      <GenericTable columns={columns} isEmpty={paginated.length === 0} emptyText="目前沒有檔案">
        {paginated.map(renderRow)}
      </GenericTable>

      <TablePager
        mt="50px"
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={safePage}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setCurrentPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setCurrentPage(1);
        }}
        labels={{
          show: '顯示',
          itemsUnit: '筆',
          range: (s, e) => `${s} - ${e} 筆`,
          prevPage: '上一頁',
          nextPage: '下一頁',
        }}
      />
    </Flex>
  );
};

export default FileManagerTable;
